import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Compass } from "lucide-react";
import api from "../lib/api";
import { vibrantTheme } from "./common";
import { SocialPulseGuide } from "./SocialPulseGuide";
import { geoSlug } from "../pages/ExploreGuide";

/* Cascading Country -> State -> City picker backed by the TravelerPulse global geo index. */
export const GeoExplorer = () => {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [cityQ, setCityQ] = useState("");
  const theme = vibrantTheme("geo-explorer");

  useEffect(() => {
    api.get("/geo/countries").then((r) => setCountries(r.data.items)).catch(() => {});
  }, []);

  useEffect(() => {
    setStates([]); setCities([]); setState(""); setCity(""); setCityQ("");
    if (!country) return;
    api.get("/geo/states", { params: { country } }).then((r) => setStates(r.data.items)).catch(() => {});
  }, [country]);

  const loadCities = (search) => {
    if (!country) return;
    api.get("/geo/cities", { params: { country, ...(state ? { state } : {}), ...(search ? { q: search } : {}), limit: 300 } })
      .then((r) => setCities(r.data.items)).catch(() => {});
  };

  useEffect(() => {
    setCities([]); setCity("");
    if (!country || !state) return;
    loadCities(cityQ.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, state]);

  const sel = "border border-zinc-300 bg-white px-3 py-2.5 text-sm font-600 w-full";

  return (
    <div className="border border-zinc-300 bg-white p-5 sm:p-6 mb-8 border-l-4" style={{ borderLeftColor: theme.bg }}
      data-testid="geo-explorer">
      <div className="flex items-center gap-2 mb-1">
        <MapPin className="w-5 h-5" style={{ color: theme.bg }} />
        <h2 className="font-heading font-900 text-xl">Explore any place worldwide</h2>
      </div>
      <p className="text-sm text-zinc-700 mb-4">Pick a country, state and city — 229,000+ cities, towns and villages in our global index.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <select value={country} onChange={(e) => setCountry(e.target.value)} className={sel} data-testid="geo-country-select">
          <option value="">Country ({countries.length})</option>
          {countries.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
        </select>
        <select value={state} onChange={(e) => setState(e.target.value)} className={sel} disabled={!states.length} data-testid="geo-state-select">
          <option value="">{states.length ? `State / region (${states.length})` : "State / region"}</option>
          {states.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
        </select>
        <select value={city} onChange={(e) => setCity(e.target.value)} className={sel} disabled={!cities.length} data-testid="geo-city-select">
          <option value="">{cities.length ? `City (${cities.length})` : "City"}</option>
          {cities.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
        </select>
      </div>
      {country && (
        <div className="mt-3 flex gap-2 max-w-md">
          <input value={cityQ} onChange={(e) => setCityQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadCities(cityQ.trim())}
            placeholder={`Find a city, town or village in ${state || country}…`}
            className={sel} data-testid="geo-city-search" />
          <button onClick={() => loadCities(cityQ.trim())} data-testid="geo-city-search-go"
            className="px-4 py-2 bg-[#4285F4] text-white text-sm font-800 hover:opacity-90 whitespace-nowrap">Go&nbsp;&rsaquo;</button>
        </div>
      )}
      {country && (
        <div className="mt-4">
          <Link to={`/explore/${[country, state, city].filter(Boolean).map(geoSlug).join("/")}`}
            data-testid="explore-geo-link"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#4285F4] text-white text-sm font-700 hover:opacity-90">
            <Compass className="w-4 h-4" /> Explore {city || state || country} — full travel guide
          </Link>
        </div>
      )}
      {country && (city || state) && (
        <div className="mt-4">
          <SocialPulseGuide country={country} state={state || undefined} city={city || undefined} />
        </div>
      )}
    </div>
  );
};
